/**
 * Liturgical Calendar Engine
 *
 * Computes the complete Roman Catholic liturgical calendar for any year,
 * following the General Norms for the Liturgical Year and the Calendar (GNLYC)
 * and the General Roman Calendar as revised after Vatican II (1969).
 *
 * Handles:
 * - Movable feasts (Easter and all dependent dates)
 * - Advent, Christmas, Lent, Easter Triduum, Easter, Ordinary Time
 * - Solemnities, Feasts, Memorials, Optional Memorials
 * - Sunday and Weekday lectionary cycles
 * - Liturgical colors
 * - US adaptations (Ascension on Sunday, etc.)
 */

import {
  computeEaster,
  addDays,
  isSameDay,
  isSunday,
  formatDateKey,
  nextWeekday,
  daysBetween,
} from './computus';
import type {
  LiturgicalDay,
  LiturgicalSeason,
  LiturgicalColor,
  CelebrationRank,
  SundayCycle,
  WeekdayCycle,
} from './types';
import { FIXED_CELEBRATIONS } from './sanctoral';

// ────────────────────────────────────────────────────────
// Key date computations for a given civil year
// ────────────────────────────────────────────────────────

export interface LiturgicalYearDates {
  /** Advent I of the *previous* liturgical year (in this civil year's early part) */
  adventStart: Date;
  /** Easter Sunday */
  easter: Date;
  /** Ash Wednesday (46 days before Easter) */
  ashWednesday: Date;
  /** Palm Sunday */
  palmSunday: Date;
  /** Holy Thursday */
  holyThursday: Date;
  /** Good Friday */
  goodFriday: Date;
  /** Holy Saturday */
  holySaturday: Date;
  /** Ascension (7th Sunday of Easter in US) */
  ascension: Date;
  /** Pentecost */
  pentecost: Date;
  /** Trinity Sunday */
  trinitySunday: Date;
  /** Corpus Christi (Sunday after Trinity in US) */
  corpusChristi: Date;
  /** Sacred Heart (Friday after Corpus Christi) */
  sacredHeart: Date;
  /** Christ the King (last Sunday before Advent I) */
  christTheKing: Date;
  /** First Sunday of Advent for the *next* liturgical year */
  nextAdventStart: Date;
  /** Christmas */
  christmas: Date;
  /** Baptism of the Lord (Sunday after Epiphany, or Monday Jan 8/9 if Epiphany on Jan 7/8) */
  baptismOfLord: Date;
}

/** Compute all key liturgical dates for a given civil year */
export function computeKeyDates(year: number): LiturgicalYearDates {
  const easter = computeEaster(year);
  const ashWednesday = addDays(easter, -46);
  const palmSunday = addDays(easter, -7);
  const holyThursday = addDays(easter, -3);
  const goodFriday = addDays(easter, -2);
  const holySaturday = addDays(easter, -1);

  // US: Ascension is transferred to 7th Sunday of Easter (40th day = Thursday, moved to Sunday)
  const ascension = addDays(easter, 42); // 7th Sunday = 6 weeks after Easter
  const pentecost = addDays(easter, 49);
  const trinitySunday = addDays(easter, 56);
  const corpusChristi = addDays(easter, 63); // Sunday after Trinity in US
  const sacredHeart = addDays(easter, 68); // Friday after Corpus Christi

  const christmas = new Date(year, 11, 25); // Dec 25

  // First Sunday of Advent: the Sunday closest to Nov 30 (on or before Dec 3)
  // = the 4th Sunday before Christmas
  const nextAdventStart = computeAdventStart(year);

  // Christ the King: last Sunday before Advent I
  const christTheKing = addDays(nextAdventStart, -7);

  // Advent I from previous year's computation that falls in January's context is irrelevant;
  // we compute it for the current year's advent (which begins the NEXT liturgical year)
  const adventStart = computeAdventStart(year - 1);

  // Epiphany in US: celebrated on the Sunday between Jan 2 and Jan 8
  const epiphany = computeEpiphanyUS(year);

  // Baptism of the Lord: Sunday after Epiphany, unless Epiphany is Jan 7 or 8, then it's the Monday
  let baptismOfLord: Date;
  if (epiphany.getDate() === 7 || epiphany.getDate() === 8) {
    baptismOfLord = addDays(epiphany, 1); // Monday after
  } else {
    baptismOfLord = nextWeekday(addDays(epiphany, 1), 0); // next Sunday
  }

  return {
    adventStart,
    easter,
    ashWednesday,
    palmSunday,
    holyThursday,
    goodFriday,
    holySaturday,
    ascension,
    pentecost,
    trinitySunday,
    corpusChristi,
    sacredHeart,
    christTheKing,
    nextAdventStart,
    christmas,
    baptismOfLord,
  };
}

/** Compute the first Sunday of Advent for a given year */
function computeAdventStart(year: number): Date {
  const christmas = new Date(year, 11, 25);
  const christmasDay = christmas.getDay(); // 0=Sun ... 6=Sat
  // Advent I is the 4th Sunday before Christmas
  // If Christmas is Sunday, Advent I is Nov 27
  // Otherwise, find the Sunday on or before Dec 3
  const offset = christmasDay === 0 ? 28 : 21 + christmasDay;
  return addDays(christmas, -offset);
}

/** Compute Epiphany date for US (Sunday between Jan 2-8) */
function computeEpiphanyUS(year: number): Date {
  const jan2 = new Date(year, 0, 2);
  return nextWeekday(jan2, 0); // First Sunday on or after Jan 2
}

// ────────────────────────────────────────────────────────
// Lectionary cycle computation
// ────────────────────────────────────────────────────────

/**
 * Determine the Sunday lectionary cycle for a liturgical year.
 * The liturgical year begins at Advent I.
 * Cycle A = years divisible by 3 with remainder 1 (2023, 2026, 2029...)
 * Cycle B = years divisible by 3 with remainder 2 (2024, 2027, 2030...)
 * Cycle C = years divisible by 3 with remainder 0 (2025, 2028, 2031...)
 */
export function getSundayCycle(liturgicalYear: number): SundayCycle {
  const r = liturgicalYear % 3;
  if (r === 1) return 'A';
  if (r === 2) return 'B';
  return 'C';
}

/**
 * Determine the weekday lectionary cycle for a liturgical year.
 * Year I = odd years, Year II = even years
 */
export function getWeekdayCycle(liturgicalYear: number): WeekdayCycle {
  return liturgicalYear % 2 === 1 ? 'I' : 'II';
}

/**
 * Get the liturgical year number for a given date.
 * The liturgical year begins at First Sunday of Advent.
 */
export function getLiturgicalYear(date: Date): number {
  const year = date.getFullYear();
  const adventStart = computeAdventStart(year);
  // If we're on or after Advent I of this civil year, the liturgical year is year+1
  if (date >= adventStart) {
    return year + 1;
  }
  return year;
}

// ────────────────────────────────────────────────────────
// Season determination
// ────────────────────────────────────────────────────────

export function determineSeason(date: Date, keyDates: LiturgicalYearDates): LiturgicalSeason {
  const year = date.getFullYear();
  const epiphany = computeEpiphanyUS(year);
  const baptism = keyDates.baptismOfLord;

  // Advent: from Advent I (previous year's computation) to Dec 24
  // For dates in early January, check if we're still in Christmas season
  if (date.getMonth() === 11) {
    // December
    if (date >= keyDates.nextAdventStart) return 'advent';
    // Dec 25 onward = Christmas season
    if (date.getDate() >= 25) return 'christmas';
  }

  // January: Christmas season until Baptism of the Lord
  if (date.getMonth() === 0) {
    if (date <= baptism) return 'christmas';
  }

  // Easter Triduum: Holy Thursday evening through Easter Sunday
  if (isSameDay(date, keyDates.holyThursday) ||
      isSameDay(date, keyDates.goodFriday) ||
      isSameDay(date, keyDates.holySaturday)) {
    return 'easter-triduum';
  }

  // Lent: Ash Wednesday through Holy Thursday (morning)
  if (date >= keyDates.ashWednesday && date < keyDates.holyThursday) {
    return 'lent';
  }

  // Easter: Easter Sunday through Pentecost
  if (date >= keyDates.easter && date <= keyDates.pentecost) {
    return 'easter';
  }

  // Check Advent (current year)
  if (date >= keyDates.nextAdventStart) return 'advent';

  // Ordinary Time (everything else)
  return 'ordinary-time';
}

// ────────────────────────────────────────────────────────
// Week of season computation
// ────────────────────────────────────────────────────────

function computeWeekOfSeason(date: Date, season: LiturgicalSeason, keyDates: LiturgicalYearDates): number {
  const year = date.getFullYear();

  switch (season) {
    case 'advent': {
      const adventStart = date.getMonth() >= 10
        ? keyDates.nextAdventStart
        : computeAdventStart(year - 1);
      return Math.floor(daysBetween(adventStart, date) / 7) + 1;
    }

    case 'christmas': {
      // Christmas season: Week 1 = Dec 25-31, Week 2 = Jan 1 onward
      if (date.getMonth() === 11) return 1; // December
      return 2; // January (until Baptism)
    }

    case 'lent': {
      // Ash Wednesday = "Week 0" (days before first Sunday)
      const firstSundayOfLent = nextWeekday(addDays(keyDates.ashWednesday, 1), 0);
      if (date < firstSundayOfLent) return 0;
      return Math.floor(daysBetween(firstSundayOfLent, date) / 7) + 1;
    }

    case 'easter-triduum':
      return 0;

    case 'easter': {
      return Math.floor(daysBetween(keyDates.easter, date) / 7) + 1;
    }

    case 'ordinary-time': {
      const baptism = keyDates.baptismOfLord;
      // Early OT: after Baptism of Lord to Ash Wednesday
      if (date < keyDates.ashWednesday) {
        const dayAfterBaptism = addDays(baptism, 1);
        return Math.floor(daysBetween(dayAfterBaptism, date) / 7) + 1;
      }
      // Late OT: after Pentecost to Advent
      // We count backwards from Christ the King (Week 34)
      const daysToChristKing = daysBetween(date, keyDates.christTheKing);
      const weeksToEnd = Math.floor(daysToChristKing / 7);
      return 34 - weeksToEnd;
    }
  }
}

// ────────────────────────────────────────────────────────
// Liturgical color determination
// ────────────────────────────────────────────────────────

function determineColor(
  date: Date,
  season: LiturgicalSeason,
  rank: CelebrationRank,
  weekOfSeason: number,
  name: string,
): LiturgicalColor {
  // Special feast colors override season
  if (name.includes('Pentecost') && isSunday(date)) return 'red';
  if (name.includes('Palm Sunday')) return 'red';
  if (name.includes('Good Friday')) return 'red';
  if (name.includes('Passion')) return 'red';
  if (name.includes('martyr') || name.includes('Martyr')) return 'red';
  if (name.includes('Apostle') || name.includes('apostle')) return 'red';
  if (name.includes('Evangelist')) return 'red';
  if (name.includes('Holy Cross') || name.includes('Exaltation')) return 'red';
  if (name.includes('Holy Innocents')) return 'red';

  // Rose for Gaudete (Advent 3) and Laetare (Lent 4) Sundays
  if (season === 'advent' && weekOfSeason === 3 && isSunday(date)) return 'rose';
  if (season === 'lent' && weekOfSeason === 4 && isSunday(date)) return 'rose';

  // Solemnities and feasts of the Lord, BVM, saints (not martyrs) = white
  if (rank === 'solemnity' || rank === 'feast') return 'white';
  if (rank === 'memorial' && !name.includes('martyr') && !name.includes('Martyr')) return 'white';

  // Season colors
  switch (season) {
    case 'advent': return 'purple';
    case 'lent': return 'purple';
    case 'easter-triduum':
      if (isSameDay(date, new Date(date.getFullYear(), date.getMonth(), date.getDate()))) {
        // Holy Thursday = white, Good Friday = red, Holy Saturday = no color (vigil = white)
        const keyDates = computeKeyDates(date.getFullYear());
        if (isSameDay(date, keyDates.holyThursday)) return 'white';
        if (isSameDay(date, keyDates.goodFriday)) return 'red';
        return 'white'; // Holy Saturday / Easter Vigil
      }
      return 'white';
    case 'christmas': return 'white';
    case 'easter': return 'white';
    case 'ordinary-time': return 'green';
  }
}

// ────────────────────────────────────────────────────────
// Day name generation
// ────────────────────────────────────────────────────────

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const ORDINALS = [
  '', 'First', 'Second', 'Third', 'Fourth', 'Fifth', 'Sixth', 'Seventh', 'Eighth',
  'Ninth', 'Tenth', 'Eleventh', 'Twelfth', 'Thirteenth', 'Fourteenth', 'Fifteenth',
  'Sixteenth', 'Seventeenth', 'Eighteenth', 'Nineteenth', 'Twentieth',
  'Twenty-First', 'Twenty-Second', 'Twenty-Third', 'Twenty-Fourth', 'Twenty-Fifth',
  'Twenty-Sixth', 'Twenty-Seventh', 'Twenty-Eighth', 'Twenty-Ninth', 'Thirtieth',
  'Thirty-First', 'Thirty-Second', 'Thirty-Third', 'Thirty-Fourth',
];

const SEASON_NAMES: Record<LiturgicalSeason, string> = {
  'advent': 'Advent',
  'christmas': 'the Christmas Season',
  'ordinary-time': 'Ordinary Time',
  'lent': 'Lent',
  'easter-triduum': 'the Easter Triduum',
  'easter': 'Easter',
};

function generateDayName(
  date: Date,
  season: LiturgicalSeason,
  weekOfSeason: number,
  keyDates: LiturgicalYearDates,
): string {
  const dow = date.getDay();
  const weekdayName = WEEKDAYS[dow];

  // Special named days
  if (isSameDay(date, keyDates.ashWednesday)) return 'Ash Wednesday';
  if (isSameDay(date, keyDates.palmSunday)) return 'Palm Sunday of the Passion of the Lord';
  if (isSameDay(date, keyDates.holyThursday)) return 'Thursday of the Lord\'s Supper';
  if (isSameDay(date, keyDates.goodFriday)) return 'Friday of the Passion of the Lord';
  if (isSameDay(date, keyDates.holySaturday)) return 'Holy Saturday / The Easter Vigil';
  if (isSameDay(date, keyDates.easter)) return 'Easter Sunday of the Resurrection of the Lord';
  if (isSameDay(date, keyDates.ascension)) return 'The Ascension of the Lord';
  if (isSameDay(date, keyDates.pentecost)) return 'Pentecost Sunday';
  if (isSameDay(date, keyDates.trinitySunday)) return 'The Most Holy Trinity';
  if (isSameDay(date, keyDates.corpusChristi)) return 'The Most Holy Body and Blood of Christ';
  if (isSameDay(date, keyDates.sacredHeart)) return 'The Most Sacred Heart of Jesus';
  if (isSameDay(date, keyDates.christTheKing)) return 'Our Lord Jesus Christ, King of the Universe';

  // Christmas
  if (date.getMonth() === 11 && date.getDate() === 25) return 'The Nativity of the Lord';

  // Epiphany
  const epiphany = computeEpiphanyUS(date.getFullYear());
  if (isSameDay(date, epiphany)) return 'The Epiphany of the Lord';

  // Baptism of the Lord
  if (isSameDay(date, keyDates.baptismOfLord)) return 'The Baptism of the Lord';

  // Holy Family (Sunday in Christmas Octave, or Dec 30 if Christmas is Sunday)
  if (season === 'christmas') {
    const christmas = new Date(date.getFullYear(), 11, 25);
    if (christmas.getDay() === 0) {
      // Christmas is Sunday, Holy Family = Dec 30
      if (date.getMonth() === 11 && date.getDate() === 30) {
        return 'The Holy Family of Jesus, Mary, and Joseph';
      }
    } else if (date.getMonth() === 11 && dow === 0 && date.getDate() > 25 && date.getDate() <= 31) {
      return 'The Holy Family of Jesus, Mary, and Joseph';
    }
  }

  // Mary, Mother of God
  if (date.getMonth() === 0 && date.getDate() === 1) return 'Solemnity of Mary, the Holy Mother of God';

  // Easter Octave days
  if (season === 'easter' && weekOfSeason === 1 && dow !== 0) {
    return `${weekdayName} within the Octave of Easter`;
  }

  // Divine Mercy Sunday
  if (season === 'easter' && weekOfSeason === 2 && dow === 0) {
    return 'Second Sunday of Easter (Divine Mercy Sunday)';
  }

  // Sundays
  if (dow === 0) {
    const ordinal = ORDINALS[weekOfSeason] || `${weekOfSeason}th`;
    return `${ordinal} Sunday of ${SEASON_NAMES[season]}`;
  }

  // Ash Wednesday week (before Lent 1 Sunday)
  if (season === 'lent' && weekOfSeason === 0) {
    return `${weekdayName} after Ash Wednesday`;
  }

  // Holy Week
  if (season === 'lent' && weekOfSeason === 6) {
    return `${weekdayName} of Holy Week`;
  }

  // Weekdays
  const ordinal = ORDINALS[weekOfSeason] || `${weekOfSeason}th`;
  return `${weekdayName} of the ${ordinal} Week of ${SEASON_NAMES[season]}`;
}

// ────────────────────────────────────────────────────────
// Sanctoral / Fixed feast overrides
// ────────────────────────────────────────────────────────

function checkFixedCelebrations(date: Date): {
  name: string;
  rank: CelebrationRank;
  color?: LiturgicalColor;
  holyDay?: boolean;
} | undefined {
  const key = `${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  return FIXED_CELEBRATIONS[key];
}

// ────────────────────────────────────────────────────────
// Main calendar computation
// ────────────────────────────────────────────────────────

/**
 * Compute the liturgical day for a given date.
 */
export function getLiturgicalDay(date: Date): LiturgicalDay {
  const year = date.getFullYear();
  const keyDates = computeKeyDates(year);
  const litYear = getLiturgicalYear(date);
  const sundayCycle = getSundayCycle(litYear);
  const weekdayCycle = getWeekdayCycle(litYear);

  const season = determineSeason(date, keyDates);
  const weekOfSeason = computeWeekOfSeason(date, season, keyDates);

  let name = generateDayName(date, season, weekOfSeason, keyDates);
  let rank: CelebrationRank = isSunday(date) ? 'solemnity' : 'weekday';
  let saintInfo: string | undefined;
  let holyDayOfObligation: boolean | undefined;

  // Check for fixed celebrations (saints, solemnities)
  const fixed = checkFixedCelebrations(date);
  if (fixed) {
    // Solemnities and feasts override weekdays and most Sundays
    if (fixed.rank === 'solemnity') {
      name = fixed.name;
      rank = 'solemnity';
      holyDayOfObligation = fixed.holyDay;
    } else if (fixed.rank === 'feast' && !isSunday(date)) {
      name = fixed.name;
      rank = 'feast';
    } else if (fixed.rank === 'memorial' && !isSunday(date) && rank === 'weekday') {
      name = fixed.name;
      rank = 'memorial';
    } else if (fixed.rank === 'optional-memorial' && !isSunday(date) && rank === 'weekday') {
      saintInfo = fixed.name;
      rank = 'weekday'; // Optional memorials don't change the day's rank
    }
  }

  // Some specific movable solemnities already named above override rank
  const movableSolemnities = [
    'easter', 'ascension', 'pentecost', 'trinitySunday', 'corpusChristi',
    'sacredHeart', 'christTheKing',
  ];
  for (const key of movableSolemnities) {
    if (isSameDay(date, keyDates[key as keyof LiturgicalYearDates] as Date)) {
      rank = 'solemnity';
      break;
    }
  }

  // Easter Triduum is sui generis
  if (season === 'easter-triduum') rank = 'solemnity';
  // Christmas, Epiphany, etc.
  if (name.includes('Nativity of the Lord')) rank = 'solemnity';
  if (name.includes('Epiphany')) rank = 'solemnity';
  if (name.includes('Baptism of the Lord')) rank = 'feast';
  if (name.includes('Holy Family')) rank = 'feast';
  if (name.includes('Octave of Easter')) rank = 'solemnity';

  // Determine fasting
  let fasting: LiturgicalDay['fasting'];
  if (isSameDay(date, keyDates.ashWednesday)) fasting = 'fast-and-abstinence';
  else if (isSameDay(date, keyDates.goodFriday)) fasting = 'fast-and-abstinence';
  else if (season === 'lent' && date.getDay() === 5) fasting = 'abstinence';
  else if (date.getDay() === 5 && season !== 'easter') fasting = 'recommended';

  const color = fixed?.color ?? determineColor(date, season, rank, weekOfSeason, name);

  return {
    date: new Date(date),
    name,
    season,
    color,
    rank,
    weekOfSeason,
    sundayCycle,
    weekdayCycle,
    saintInfo,
    holyDayOfObligation,
    fasting,
  };
}

/**
 * Get all liturgical days for a given month.
 */
export function getMonth(year: number, month: number): LiturgicalDay[] {
  const days: LiturgicalDay[] = [];
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  for (let d = 1; d <= daysInMonth; d++) {
    days.push(getLiturgicalDay(new Date(year, month, d)));
  }
  return days;
}

/**
 * Get all liturgical days for a given year.
 */
export function getYear(year: number): LiturgicalDay[] {
  const days: LiturgicalDay[] = [];
  for (let month = 0; month < 12; month++) {
    days.push(...getMonth(year, month));
  }
  return days;
}

/** Get the liturgical day for today */
export function getToday(): LiturgicalDay {
  return getLiturgicalDay(new Date());
}

/** Format the date key for a given Date object */
export { formatDateKey };
